import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { errorText, saveBlob } from '@/app/client-reports/_components/format';
import {
  ContentBackground,
  ContentWrapper,
  ContentHeader,
  ContentBody,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES, ROLES_PERMISSIONS } from '@/constants';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';

const { Text, Title } = Typography;

/**
 * The client's side of the monthly reports: what their accountant published,
 * by company and month, newest first.
 *
 * @returns {JSX.Element} The page
 */
function MyReportsContent() {
  const { t } = useTranslation(['clientReports', 'menus']);
  // Operators reach this page by address and the server hands them every
  // company's releases, so they must always see whose month they are looking
  // at; a client with a single company does not need its own name repeated.
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const isOperator =
    userRoles.includes(ROLES.techAdmin) || userRoles.includes(ROLES.superAdmin);

  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-my-releases'],
    queryFn: () => API.clientReports.getMyReleases(),
  });

  const downloadMutation = useMutation({
    mutationFn: ({ releaseId, index }) =>
      API.clientReports.downloadMyReleaseFile(releaseId, index),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  // One block per company, months newest first (the server already sorts).
  const companies = useMemo(() => {
    const byCompany = new Map();
    for (const release of data?.data || []) {
      const id = release.company?._id || release.company;
      if (!byCompany.has(id))
        byCompany.set(id, {
          id,
          name: release.company?.name || '',
          releases: [],
        });
      byCompany.get(id).releases.push(release);
    }
    return [...byCompany.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  return (
    <RootLayout metadata={{ title: t('myReports', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('myReports', { ns: 'menus' })} />

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <p className="mb-4 text-sm opacity-70">{t('my.subtitle')}</p>

                  {isLoading && <Spin />}

                  {!isLoading && !companies.length && (
                    <Empty description={t('my.none')} />
                  )}

                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: '100%' }}
                  >
                    {companies.map((company) => (
                      <Card key={company.id} size="small">
                        {(companies.length > 1 || isOperator) && (
                          <Title level={5} style={{ marginTop: 0 }}>
                            {company.name}
                          </Title>
                        )}
                        <List
                          dataSource={company.releases}
                          renderItem={(release) => (
                            <List.Item>
                              <div style={{ width: '100%' }}>
                                <Space
                                  wrap
                                  size={8}
                                  style={{ marginBottom: 6 }}
                                >
                                  <Text strong>
                                    {`${String(release.month).padStart(2, '0')}.${release.year}`}
                                  </Text>
                                  {release.version > 1 && (
                                    <Tag color="orange">
                                      {t('my.corrected')}
                                    </Tag>
                                  )}
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    {t('my.publishedAt', {
                                      date: dayjs(release.publishedAt).format(
                                        'DD.MM.YYYY'
                                      ),
                                    })}
                                  </Text>
                                </Space>
                                {release.note && (
                                  <Alert
                                    type="info"
                                    style={{
                                      marginBottom: 8,
                                      whiteSpace: 'pre-wrap',
                                    }}
                                    message={release.note}
                                  />
                                )}
                                <Space wrap size={8}>
                                  {release.files.map((file) => (
                                    <Button
                                      key={file.index}
                                      shape="round"
                                      icon={
                                        <FileExcelOutlined
                                          style={{ color: '#1d6f42' }}
                                        />
                                      }
                                      loading={
                                        downloadMutation.isPending &&
                                        downloadMutation.variables
                                          ?.releaseId === release._id &&
                                        downloadMutation.variables?.index ===
                                          file.index
                                      }
                                      onClick={() =>
                                        downloadMutation.mutate({
                                          releaseId: release._id,
                                          index: file.index,
                                        })
                                      }
                                    >
                                      {file.fileName}
                                      <DownloadOutlined />
                                    </Button>
                                  ))}
                                </Space>
                              </div>
                            </List.Item>
                          )}
                        />
                      </Card>
                    ))}
                  </Space>
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

const MyReportsPageWithAuth = withPrivateRoute(
  MyReportsContent,
  ROLES_PERMISSIONS.CLIENT_REPORTS.MY
);
MyReportsPageWithAuth.displayName = 'MyReportsPage';

export default MyReportsPageWithAuth;
